import {
  Box,
  Flex,
  SearchField,
  Select,
  SelectItem,
  Text,
} from '@backstage/ui';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { ownerToEntityRef } from '../../utils/ownerToEntityRef';
import {
  CHECK_RESULT_DISPLAY,
  type CheckResultDisplay,
  FILTERABLE_CHECK_RESULTS,
  toCheckResultDisplay,
} from './checkResult';
import { CheckResultBadge } from './CheckResultBadge';
import type { CheckRow } from '../../hooks/useProjectScorecard';
import { type ColumnDef, LeveledTable } from '../LeveledTable/LeveledTable';
import { useSeverities } from '../../hooks/useSeverities';
import { useCallback, useMemo, useState } from 'react';
import { useScorecardsFilter } from './ScorecardsFilterContext';
import styles from './ChecksTable.module.css';

const STATUS_ITEMS: { id: CheckResultDisplay | ''; label: string }[] = [
  { id: '', label: 'All statuses' },
  ...FILTERABLE_CHECK_RESULTS.map(id => ({
    id,
    label: CHECK_RESULT_DISPLAY[id].label,
  })),
];

const statusSortOrder = (row: CheckRow) =>
  CHECK_RESULT_DISPLAY[toCheckResultDisplay(row.snapshot)].sortOrder;

const resultSearchText = (row: CheckRow) =>
  CHECK_RESULT_DISPLAY[toCheckResultDisplay(row.snapshot)].label.toLowerCase();

interface ChecksTableProps {
  rows: CheckRow[];
  isLoading: boolean;
  onRowClick: (row: CheckRow) => void;
}

export const ChecksTable = ({
  rows,
  isLoading,
  onRowClick,
}: ChecksTableProps) => {
  const { activeSeverities, activeCategories } = useScorecardsFilter();
  const { severities } = useSeverities();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<CheckResultDisplay | ''>('');

  const columns = useMemo<ColumnDef<CheckRow>[]>(
    () => [
      {
        id: 'check',
        label: 'Check',
        width: '1fr',
        isRowHeader: true,
        allowsSorting: true,
        renderCell: row => (
          <button
            className={styles.checkNameButton}
            onClick={() => onRowClick(row)}
          >
            {row.check.name}
          </button>
        ),
      },
      {
        id: 'track',
        label: 'Track',
        renderCell: row => (
          <Text as="span" variant="body-medium">
            {row.check.trackName}
          </Text>
        ),
      },
      {
        id: 'owner',
        label: 'Owner',
        renderCell: row => {
          const { owner } = row.check;
          if (!owner)
            return (
              <Text as="span" color="secondary">
                -
              </Text>
            );
          return <EntityRefLink entityRef={ownerToEntityRef(owner)} />;
        },
      },
      {
        id: 'status',
        label: 'Status',
        allowsSorting: true,
        renderCell: row => (
          <CheckResultBadge result={toCheckResultDisplay(row.snapshot)} />
        ),
      },
    ],
    [onRowClick],
  );

  const compare = useCallback(
    (a: CheckRow, b: CheckRow, column: string): number => {
      if (column === 'check') return a.check.name.localeCompare(b.check.name);
      if (column === 'status') return statusSortOrder(a) - statusSortOrder(b);
      return 0;
    },
    [],
  );

  const filteredRows = useMemo(() => {
    let result = rows;
    if (activeSeverities.size)
      result = result.filter(r => activeSeverities.has(r.check.severity));
    if (activeCategories.size)
      result = result.filter(r => activeCategories.has(r.check.trackId));
    if (statusFilter)
      result = result.filter(
        r => toCheckResultDisplay(r.snapshot) === statusFilter,
      );
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(row => {
        const sv = severities.find(s => s.id === row.check.severity);
        const sevText = (sv?.label ?? row.check.severity).toLowerCase();
        return (
          row.check.name.toLowerCase().includes(q) ||
          (row.check.description ?? '').toLowerCase().includes(q) ||
          row.check.trackName.toLowerCase().includes(q) ||
          sevText.includes(q) ||
          resultSearchText(row).includes(q)
        );
      });
    }
    return result;
  }, [
    rows,
    activeSeverities,
    activeCategories,
    statusFilter,
    search,
    severities,
  ]);

  const toolbar = (
    <Flex gap="3" align="center" justify="end">
      <Box>
        <SearchField
          value={search}
          onChange={setSearch}
          aria-label="Search checks"
        />
      </Box>
      <Box shrink={false}>
        <Select
          items={STATUS_ITEMS}
          value={statusFilter}
          onChange={key =>
            setStatusFilter(
              String(key) === '' ? '' : (String(key) as CheckResultDisplay),
            )
          }
        >
          {(item: (typeof STATUS_ITEMS)[number]) => (
            <SelectItem id={item.id} textValue={item.label}>
              {item.id ? <CheckResultBadge result={item.id} /> : item.label}
            </SelectItem>
          )}
        </Select>
      </Box>
    </Flex>
  );

  if (isLoading) {
    return <Text as="p">Loading...</Text>;
  }

  return (
    <LeveledTable
      items={filteredRows}
      getLevelId={row => row.snapshot?.levelId ?? null}
      getItemKey={row => row.id}
      columns={columns}
      compare={compare}
      defaultSort={{ column: 'status', direction: 'ascending' }}
      toolbar={toolbar}
      emptyState={
        <Text as="p" color="secondary">
          No checks found for this entity.
        </Text>
      }
    />
  );
};
